import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { COLORED_ROUTE, SurfaceEnum, WATCHES_WARNINGS_ADVISORIES } from "../UserVisibleStrings";
import Legend from "./bottombar/Legend";
import { calculateLogoLeft, getComponentNames, logoHeight, logoWidth, ZIndexLevels } from "./geoUtils";
import { useAppDispatch } from "../../app/hooks";
import { changeSurface, useSelectedChip } from "../../features/surface/surfaceSlice";
import { useIsMobile } from "../../features/pageWidth/pageWidthSlice";
import { useIsLeftBarExpanded, useIsRightBarExpanded } from "../../features/Drawer/drawerSlice";
import { AllForecastComponents } from "../../app/forecastSurface";
import rv_logo from "../../static/RVWeather.svg";
import AdditionalActions from "./bottombar/AdditionalActions";
import { MapRef } from "react-map-gl";
import ImpactSurfaceDropdown from "../sidebar/ImpactSurfaceDropdown";
import {
    RiskViewerKind,
    RouteViewState,
    toggleRouteViewState,
    useRiskViewerStateKind,
    useRouteViewState
} from "../../features/riskViewer/riskViewerSlice";
import { hideAlerts, showAlerts, useShouldShowAlerts } from "../../features/alertDisplay/alertDisplaySlice";
import MapIconLegend from "../sidebar/MapIconLegend";
import { useLayerVisibilityState } from "../../features/mapDisplay/mapDisplaySlice";
import { setIsPlaying } from "../../features/time/playbackSlice";


type GeoViewOverlayProps = {
    allComponents: AllForecastComponents | undefined,
    mapRef: React.RefObject<MapRef>,
};

const GeoViewOverlay: React.FC<GeoViewOverlayProps> = ( {
    allComponents,
    mapRef,
} ) =>
{
    const isMobile = useIsMobile( );
    const isRightBarExpanded = useIsRightBarExpanded();
    const isLeftBarExpanded = useIsLeftBarExpanded();
    const routeViewState = useRouteViewState();
    const componentNamesImpact: Array<SurfaceEnum> = useMemo( () => getComponentNames( allComponents ), [ allComponents ] );

    const riskViewerStateKind = useRiskViewerStateKind();
    const selectedSurface = useSelectedChip();
    const shouldShowAlerts = useShouldShowAlerts();
    const selectedChip = routeViewState === RouteViewState.COLORED_ROUTE ? COLORED_ROUTE : shouldShowAlerts ? WATCHES_WARNINGS_ADVISORIES : selectedSurface;

    const LayerEnabledState = useLayerVisibilityState();

    const dispatch = useAppDispatch();
    // TODO: this is not handled correctly in route view, make it a prop that gets drilled down or use some other mechanism
    // to change the handler behavior at a higher level.
    function handleClick( chipName: string )
    {
        if ( chipName === "" ) return;
        if ( chipName === WATCHES_WARNINGS_ADVISORIES )
        {
            dispatch( showAlerts() );
            dispatch( setIsPlaying( false ) );
            if ( routeViewState === RouteViewState.COLORED_ROUTE )
            {
                dispatch( toggleRouteViewState() );
            }
            return;
        }
        if ( shouldShowAlerts )
        {
            dispatch( hideAlerts() );
        }
        if ( chipName === COLORED_ROUTE && routeViewState !== RouteViewState.COLORED_ROUTE )
        {
            dispatch( toggleRouteViewState() );
            return;
        }
        else if ( chipName === COLORED_ROUTE && routeViewState === RouteViewState.COLORED_ROUTE )
        {
            return;
        }
        else if ( chipName !== COLORED_ROUTE && routeViewState === RouteViewState.COLORED_ROUTE )
        {
            dispatch( toggleRouteViewState() );
        }
        dispatch( changeSurface( chipName ) );
        dispatch( setIsPlaying( false ) );
    }

    return (
        <React.Fragment>
            <AdditionalActions mapRef = {mapRef}/>
            <Legend isMobile = {isMobile}/>

            {riskViewerStateKind === RiskViewerKind.TRIP_STATE && LayerEnabledState.IconLegend && <MapIconLegend isMobile = {isMobile}/>}
            <ImpactSurfaceDropdown selectSurface = {handleClick} selectedSurface = {selectedChip} allSurfaces = {componentNamesImpact}/>
            <a href = "https://rvweather.com/">
                <Box
                    component = "img"
                    alt = "rvwx logo"
                    src = {rv_logo}
                    sx = {{
                        position: "absolute",
                        top: "5px",
                        left: calculateLogoLeft( isMobile, isRightBarExpanded, isLeftBarExpanded ),
                        transform: isMobile ? "none" : "translateX(-50%)",
                        height: isMobile ? logoHeight / 2 : logoHeight,
                        width: isMobile ? logoWidth / 2 : logoWidth,
                        maxHeight: logoHeight,
                        maxWidth: logoWidth,
                        zIndex: ZIndexLevels.UIElement,
                        transition: "left 0.3s ease, transform 0.3s ease",
                    }}
                />
            </a>
        </React.Fragment>
    );
};

export default GeoViewOverlay;
